import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "order-container__content" }
const _hoisted_2 = { class: "order-container__order-form" }

import { computed, provide, onMounted, ref, watch } from 'vue';

import { accountService } from '@exchange/libs/account/service/src';
import marketTypeTradingEligibilityService from '@exchange/libs/account/service/src/lib/market-type-trading-eligibility.service';
import { authService } from '@exchange/libs/auth/service/src';
import { balanceService } from '@exchange/libs/balances/service/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { featureRestrictionsService } from '@exchange/libs/feature-restrictions/service/src';
import futuresFeatureFlagService from '@exchange/libs/futures/service/src/lib/futures-feature-flag.service';
import { marketService, MarketState } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import BlockedOverlay from '@exchange/libs/overlays/BlockedOverlay.vue';
import FuturesPublicTradingDisabledOverlay from '@exchange/libs/overlays/FuturesPublicTradingDisabledOverlay.vue';
import IneligibleToTradeMarketOverlay from '@exchange/libs/overlays/IneligibleToTradeMarketOverlay.vue';
import LoggedOutOverlay from '@exchange/libs/overlays/LoggedOutOverlay.vue';
import MarketMismatchOverlay from '@exchange/libs/overlays/MarketMismatchOverlay.vue';
import MarketOverlay from '@exchange/libs/overlays/MarketOverlay.vue';
import { subaccountsService, SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';

import TransferPanel from './components/TransferPanel.vue';
import orderFormEmitter from './order-form-event-bus';
import OrderForm from './OrderForm.vue';
import OrderFormSideSwitcher from './OrderFormSideSwitcher.vue';
import OrderTypeChooser from './OrderTypeChooser.vue';
import useOrderTypes from './OrderTypes';


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderContainer',
  props: {
  marketId: { type: String, required: true },
  transparentBackground: { type: Boolean, default: false },
},
  emits: ["reset-form-softly", "order-form:scroll-content"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

provide('orderFormEmitter', orderFormEmitter);

const { isXS } = useMQBreakpoints();

const { storedType, market: marketType, limit: limitType } = useOrderTypes();

const supportMarketOrders = launchdarkly.flags['market-orders'];
const { balanceLoadingUntil } = balanceService;
const { isEnabledWithPublicTradingEnabled: isFuturesEnabledWithPublicTradingEnabled, isReady: isFuturesFeatureFlagReady } = futuresFeatureFlagService;
const { getMarket, getMarketName, getMarketState } = marketService;

const orderContainerEl = ref<undefined | { $el: HTMLElement }>();
const orderContainer = ref<{
  topPosition: number;
}>({
  topPosition: 0,
});

let psScrollTriggered = 0;
const typeOptions = computed(() => (supportMarketOrders.value ? [marketType, limitType] : [limitType]));

const market = computed(() => getMarket(props.marketId));
const marketName = computed(() => getMarketName(market.value));
const marketState = computed(() => getMarketState(market.value));

const showMarketOverlayOnForm = computed(() => marketState.value && [MarketState.SUSPENDED, MarketState.IN_MAINTENANCE, MarketState.NEW].includes(marketState.value));
const { isSettingAccount, subaccountType, userIsBlocked: isBlocked } = accountService;
const { isSwitchingSubaccount } = subaccountsService;
const isLoading = computed(() => !authService.initialLoadAuthIsChecked || !market.value || isSwitchingSubaccount.value);
const userHasBeenAuthenticated = computed(() => authService.hasBeenAuthenticated);

const isFuturesMarket = computed(() => market.value?.type === MarketType.PERP);
const isSpotMarket = computed(() => market.value?.type === MarketType.SPOT);

const canShowAccountOverlay = computed(() => !isLoading.value && !isSettingAccount.value);
const canShowFuturesPublicTradingDisabledOverlay = computed(() => isFuturesFeatureFlagReady.value);

const { isLoadingMarketTypeTradingEligibility, marketTypeTradingEligibility, canTradeSpot, canTradeFutures } = marketTypeTradingEligibilityService;

const showFuturesPublicTradingDisabledOverlay = computed(() => {
  if (isSpotMarket.value || !canShowFuturesPublicTradingDisabledOverlay.value) {
    return false;
  }

  return !isFuturesEnabledWithPublicTradingEnabled.value;
});

const showIneligibleToTradeMarketOverlay = computed(() => {
  if (!canShowAccountOverlay.value || isLoadingMarketTypeTradingEligibility.value || !marketTypeTradingEligibility.value) return false;

  return isSpotMarket.value ? !canTradeSpot.value : !canTradeFutures.value;
});

const showAccountTypeMismatchOverlay = computed(() => {
  if (!canShowAccountOverlay.value || !subaccountType.value) return false;

  return (isFuturesMarket.value && subaccountType.value !== SubaccountType.FUTURES) || (isSpotMarket.value && subaccountType.value !== SubaccountType.SPOT);
});

const activeType = computed(() => storedType.value || limitType.type);

const scrollCallbacks = {
  onScroll: () => {
    psScrollTriggered += 1;
  },
};

const onActiveTypeChange = (type) => {
  if (storedType.value !== type) {
    orderformService.setType(type);
    orderFormEmitter.emit('reset-form-softly');
  }
};

const onActiveSideChange = () => {
  emit('order-form:scroll-content', orderContainer.value?.topPosition);
};

const setContainerTopPosition = () => {
  const elem = orderContainerEl.value;

  if (!elem) {
    return;
  }

  const { top } = elem.$el.getBoundingClientRect();

  orderContainer.value.topPosition = top;
};

watch(
  userHasBeenAuthenticated,
  (newUserHasBeenAuthenticated) => {
    if (newUserHasBeenAuthenticated) {
      featureRestrictionsService.get();
    }
  },
  {
    immediate: true,
  },
);

onMounted(setContainerTopPosition);

return (_ctx: any,_cache: any) => {
  const _component_ot_module_container = _resolveComponent("ot-module-container")!

  return (_openBlock(), _createBlock(_component_ot_module_container, {
    ref_key: "orderContainerEl",
    ref: orderContainerEl,
    class: "order-container",
    "log-name": "order-form",
    "scroll-callbacks": scrollCallbacks,
    variant: __props.transparentBackground ? 'transparent' : 'default',
    loading: isLoading.value
  }, {
    "header-left-actions": _withCtx(() => [
      _createVNode(TransferPanel, {
        class: "order-container__transfer-panel",
        "market-id": __props.marketId
      }, null, 8, ["market-id"]),
      _createVNode(OrderTypeChooser, {
        class: _normalizeClass(["order-container__type-chooser", { 'order-container__type-chooser--mobile': _unref(isXS) }]),
        "available-types": typeOptions.value,
        "selected-type": activeType.value,
        "is-futures": isFuturesMarket.value,
        onTypeSelected: onActiveTypeChange
      }, null, 8, ["available-types", "selected-type", "class", "is-futures"])
    ]),
    "main-content": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (!userHasBeenAuthenticated.value)
          ? (_openBlock(), _createBlock(LoggedOutOverlay, { key: 0 }))
          : (_unref(isBlocked))
            ? (_openBlock(), _createBlock(BlockedOverlay, { key: 1 }))
            : (showMarketOverlayOnForm.value)
              ? (_openBlock(), _createBlock(MarketOverlay, {
                  key: 2,
                  "market-name": marketName.value,
                  "market-state": marketState.value,
                  "has-been-authenticated": userHasBeenAuthenticated.value
                }, null, 8, ["market-name", "market-state", "has-been-authenticated"]))
              : (showFuturesPublicTradingDisabledOverlay.value)
                ? (_openBlock(), _createBlock(FuturesPublicTradingDisabledOverlay, { key: 3 }))
                : (showIneligibleToTradeMarketOverlay.value)
                  ? (_openBlock(), _createBlock(IneligibleToTradeMarketOverlay, { key: 4 }))
                  : (showAccountTypeMismatchOverlay.value)
                    ? (_openBlock(), _createBlock(MarketMismatchOverlay, {
                        key: 5,
                        "is-futures-market": isFuturesMarket.value
                      }, null, 8, ["is-futures-market"]))
                    : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(OrderFormSideSwitcher, {
            class: _normalizeClass(["order-container__side-switcher", { 'order-container__side-switcher--mobile': _unref(isXS) }]),
            "on-active-side-change": onActiveSideChange,
            "is-futures": isFuturesMarket.value
          }, null, 8, ["class", "is-futures"]),
          (market.value)
            ? (_openBlock(), _createBlock(OrderForm, {
                key: 0,
                class: "order-container__form",
                transparent: __props.transparentBackground,
                "fit-slider-tooltip-max-value": !_unref(isXS),
                "balance-loading-until": _unref(balanceLoadingUntil),
                "spot-market": market.value,
                "ps-scroll-triggered": _unref(psScrollTriggered),
                "is-futures": isFuturesMarket.value
              }, null, 8, ["transparent", "fit-slider-tooltip-max-value", "balance-loading-until", "spot-market", "ps-scroll-triggered", "is-futures"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["variant", "loading"]))
}
}

})